import { type ReactElement, type ComponentType } from 'react';
import {
  IconDeviceGamepad,
  IconDna,
  IconHome,
  IconMapPin,
  IconPlant2,
  IconDeviceGamepad2,
  IconSettings,
  type IconProps,
} from '@tabler/icons-react';
import type { AppKey } from '@mdev/common';

import { HomePage } from '../../apps/home/HomePage';
import { GardenPage } from '../../apps/garden/GardenPage';
import { HubHomePage } from '../../apps/games/routing/HubHomePage';
import { AiGeneticsPage } from '../../apps/ai_genetics/AiGeneticsPage';
import { GeoguesserPage } from '../../apps/geoguesser/GeoguesserPage';
import { DosArcadePage } from '../../apps/dos_arcade/DosArcadePage';
import { SettingsPage } from '../../apps/settings/SettingsPage';

export interface AppRoute {
  key: AppKey;
  path: string;
  label: string;
  description: string;
  Icon: ComponentType<IconProps>;
  /** True for items that should appear in the side nav. */
  inNav: boolean;
  /** True for items that should appear as tiles on the home dashboard. */
  onHome: boolean;
  render: () => ReactElement;
}

export const APP_ROUTES: readonly AppRoute[] = [
  {
    key: 'home',
    path: '/',
    label: 'Home',
    description: 'Dashboard',
    Icon: IconHome,
    inNav: true,
    onHome: false,
    render: () => <HomePage />,
  },
  {
    key: 'garden',
    path: '/garden',
    label: 'Garden',
    description: 'Mapleton garden planner — Zone 6b',
    Icon: IconPlant2,
    inNav: true,
    onHome: true,
    render: () => <GardenPage />,
  },
  {
    key: 'games',
    path: '/games/*',
    label: 'Games',
    description: 'Scotland Yard and friends',
    Icon: IconDeviceGamepad2,
    inNav: true,
    onHome: true,
    // Routing for `/games` (menu) and `/games/*` (sessions) is handled at the
    // top level in `App.tsx`; this entry exists for nav + home-tile metadata.
    render: () => <HubHomePage />,
  },
  {
    key: 'ai_genetics',
    path: '/ai-genetics',
    label: 'AI Genetics',
    description: 'Methylation, detox, and nutrition pathway analysis',
    Icon: IconDna,
    inNav: true,
    onHome: true,
    render: () => <AiGeneticsPage />,
  },
  {
    key: 'geoguesser',
    path: '/geoguesser',
    label: 'Geoguesser',
    description: 'Guess the random Street View location',
    Icon: IconMapPin,
    inNav: true,
    onHome: true,
    render: () => <GeoguesserPage />,
  },
  {
    key: 'dos_arcade',
    path: '/arcade/*',
    label: 'Arcade',
    description: 'Retro DOS games — pick a tile, hit start',
    Icon: IconDeviceGamepad,
    inNav: true,
    onHome: true,
    render: () => <DosArcadePage />,
  },
  {
    key: 'settings',
    path: '/settings',
    label: 'Settings',
    description: 'Account and preferences',
    Icon: IconSettings,
    inNav: true,
    onHome: false,
    render: () => <SettingsPage />,
  },
] as const;

export const homeTiles = APP_ROUTES.filter((r) => r.onHome);
export const navItems = APP_ROUTES.filter((r) => r.inNav);
